import React from 'react';
import { Asset } from 'contentful';
import {
  Document,
  BLOCKS,
  INLINES,
  Block,
  Inline
} from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import type { AffiliateHero2Props, greenlightBrandingEnum } from '../index';

import { getImageData } from '~/utils/contentful';
import RichText from '~/components/RichText';

import { Box } from '../../../Box';
import { Column } from '../../../Layout';
import CtaWidget from '../../../CtaWidget';
import { AffiliateLogoGrouping } from '../AffiliateLogoGrouping';
import PrivacyPolicyDisclaimer from '../../../PrivacyPolicyDisclaimer';
import cx from 'classnames';

import { DesktopImage, QrCodeWrapper, QrCodeContainer } from '../styles';

interface PropsWithOverride extends AffiliateHero2Props {
  qrContainerId?: string;
}

export default function DesktopView({
  headingText,
  useLightHeadingText,
  subheadingText,
  subheadingTextRich,
  useLightSubheadingText,
  fullwidthDesktopImage,
  desktopCta,
  affiliateLogo,
  useGreenlightIcon,
  greenlightBranding,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  sendConsentEvent,
  useEmbeddedSmartLink,
  qrContainerId
}: PropsWithOverride) {
  const visibility = fullwidthDesktopImage ? 'visible' : 'hidden';

  const SUBHEADING_RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return <>{children}</>;
    },
    [INLINES.HYPERLINK]: (node, children) => {
      const href = node.data.uri as string;
      return (
        <a
          href={href}
          className="underline transition-colors duration-200 ease-in-out hover:opacity-80"
        >
          {children}
        </a>
      );
    }
  };

  const renderSubheading = (text: string | Document | undefined) => {
    if (!text) return null;

    if (typeof text === 'string') {
      return <span>{text}</span>;
    }

    if (text && typeof text === 'object' && 'nodeType' in text) {
      return (
        <RichText
          data={text as Document}
          overrideOptions={SUBHEADING_RICH_TEXT_OVERRIDE}
        />
      );
    }

    return null;
  };

  return (
    <section
      className="desktop-block-container my-12 px-15 py-8 tablet:mt-3 tablet:flex"
      data-blockid="affiliate-hero-20-hero-desktop"
      data-e2e="hero"
    >
      <div className="layout layout-padding mt-2 !flex-nowrap gap-8">
        <div className="column flex-col items-center justify-center tablet:w-1/2">
          <div className="flex max-w-[532px] flex-col">
            {affiliateLogo && (
              <AffiliateLogoGrouping
                affiliateLogo={affiliateLogo}
                useGreenlightIcon={useGreenlightIcon}
                greenlightBranding={
                  greenlightBranding as greenlightBrandingEnum
                }
              />
            )}
            <h1
              className={cx(
                'mb-4 mt-4 text-left text-4xl desktop:text-5xl',
                useLightHeadingText ? 'text-white' : 'text-black'
              )}
              data-testid="affiliate-hero-20-headline-desktop"
            >
              {headingText}
            </h1>
            <div
              className={cx(
                'mb-6 mt-0 text-left text-xl leading-6',
                useLightSubheadingText ? 'text-white' : 'text-black'
              )}
              data-testid="affiliate-hero-20-subhead-desktop"
            >
              {renderSubheading(subheadingTextRich ?? subheadingText)}
            </div>
            {desktopCta && (
              <Box data-testid="affiliate-hero-desktop-cta">
                <CtaWidget
                  {...desktopCta?.fields}
                  id="affiliate-hero-desktop-cta-widget"
                  sysId={desktopCta?.sys?.contentType?.sys?.id}
                  module="Hero"
                  sendConsentEvent={sendConsentEvent}
                />
              </Box>
            )}

            {privacyPolicyDisclaimer && (
              <Box marginTop="ml">
                <PrivacyPolicyDisclaimer
                  privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                  displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                />
              </Box>
            )}
          </div>
        </div>
        <Column size={{ tablet: 7, desktop: 6 }}>
          <Box className="relative" flex="1">
            {useEmbeddedSmartLink ? (
              <QrCodeWrapper>
                <QrCodeContainer>
                  <div id={qrContainerId} data-testid={qrContainerId} />
                </QrCodeContainer>
              </QrCodeWrapper>
            ) : (
              <DesktopImage
                {...getImageData(fullwidthDesktopImage as Asset)}
                visibility={visibility}
                heroImage={true}
                loading="eager"
              />
            )}
          </Box>
        </Column>
      </div>
    </section>
  );
}
